import type { ReactNode } from 'react';
import { Card, BlockStack, Text, SkeletonBodyText, Banner, Button, InlineStack } from '@shopify/polaris';
import { useInstall } from '../hooks/useInstall';
import { InstallError } from '../types';

interface InstallGateProps {
  children: ReactNode;
}

/**
 * Blocks rendering the dashboard until Token Exchange has either succeeded
 * (`{status:'cached'|'exchanged'}`) or failed in a way the merchant must
 * address. Branches:
 *
 *   - loading             → skeleton card
 *   - 404 no_connection   → "Create your Unifd connection first" CTA
 *   - 502 token_exchange  → "We couldn't connect your store" + retry button
 *   - other / unknown     → generic banner + retry button
 *   - success             → render children (the dashboard tree)
 */
export function InstallGate({ children }: InstallGateProps) {
  const install = useInstall();

  if (install.isLoading) {
    return (
      <Card>
        <BlockStack gap="200">
          <Text as="p" tone="subdued">Connecting your store to Unifd…</Text>
          <SkeletonBodyText lines={2} />
        </BlockStack>
      </Card>
    );
  }

  if (install.isError) {
    const code = install.error instanceof InstallError ? install.error.code : 'unknown';

    if (code === 'no_connection_for_shop') {
      return (
        <Banner tone="warning" title="Set up your Unifd connection first">
          <BlockStack gap="200">
            <Text as="p">
              This Shopify store isn't linked to a Unifd connection yet. Open
              the Unifd portal to create one, then reload this page.
            </Text>
          </BlockStack>
        </Banner>
      );
    }

    // token_exchange_failed and unknown both offer a retry path.
    return (
      <Banner
        tone="critical"
        title={code === 'token_exchange_failed' ? "We couldn't connect your store" : 'Something went wrong'}
      >
        <BlockStack gap="200">
          <Text as="p">
            {code === 'token_exchange_failed'
              ? 'Shopify rejected the access grant. This is usually temporary — try again in a moment.'
              : 'An unexpected error occurred while connecting your store.'}
          </Text>
          <InlineStack>
            <Button onClick={() => install.refetch()} loading={install.isFetching}>
              Try again
            </Button>
          </InlineStack>
        </BlockStack>
      </Banner>
    );
  }

  return <>{children}</>;
}
